import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Search, X, Check, ArrowRight, Loader2, ChevronRight, ChevronLeft, Home, ZoomIn, Zap } from 'lucide-react'; // Added Zap icon
import useProductsData from '../hooks/useProductsData';
import { slugify, optimizeImage, generateSrcSet } from '../utils';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Schema from '../components/Schema';
import { SITE_URL } from '../data/Constants';


const MAIN_CATEGORIES = [
  'Electric',
  'Gas',
  'Luxury Gas',
  'Luxury Mantels',
  'Outdoor Fire Features',
  'Outdoor Kitchen',
];

// --- PARSE CATEGORY PATH ---
const parseCategoryPath = (filterString, allBrands) => {
  let parts = filterString.split(',').map(s => s.trim());

  if (parts.length > 0 && parts[0].toLowerCase() === 'fireplaces') {
    parts.shift();
  }

  let rawMain = parts[0].toLowerCase();
  let mainCategory = '';

  if (rawMain.includes('electric')) mainCategory = 'Electric';
  else if (rawMain.includes('luxury gas')) mainCategory = 'Luxury Gas';
  else if (rawMain.includes('luxury mantels')) mainCategory = 'Luxury Mantels';
  else if (rawMain.includes('gas')) mainCategory = 'Gas';
  else if (rawMain.includes('outdoor fire')) mainCategory = 'Outdoor Fire Features';
  else if (rawMain.includes('outdoor kitchen') || rawMain.includes('grills') || rawMain.includes('barbeque')) mainCategory = 'Outdoor Kitchen';

  const subCategories = parts.slice(1).filter(part => {
    const lowerPart = part.toLowerCase();
    const isBrand = allBrands.has(lowerPart);
    return !isBrand;
  }).map(s => s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')); // Capitalize

  return { main: mainCategory, subs: subCategories };
};



// --- HELPER: Normalize Attribute (String/Array -> Array) ---
const normalizeAttribute = (rawValue) => {
  if (!rawValue) return [];
  if (Array.isArray(rawValue)) {
    return rawValue.map(s => String(s).replace(/['"]/g, '').trim()).filter(Boolean);
  }
  const s = String(rawValue).replace(/['"]/g, '').trim();
  if (s.includes(',')) {
    return s.split(',').map(x => x.trim()).filter(Boolean);
  }
  return [s];
};

// --- HELPER: Format Power Voltage ---
const formatPowerVoltage = (rawField) => {
  if (!rawField || typeof rawField !== 'object') return null;

  // Example Input: { "2000": "'220-240'", "4000": "'208','220-240'" }
  // Desired Output: "2000W (220-240V), 4000W (208, 220-240V)"

  const formattedParts = Object.entries(rawField).map(([watts, volts]) => {
    const cleanVolts = String(volts).replace(/['"]/g, '').trim();
    return `${watts}W (${cleanVolts}V)`;
  });

  return formattedParts.length > 0 ? formattedParts.join(', ') : null;
};

// --- PRODUCT MODAL COMPONENT ---
const ProductModal = ({ product, onClose }) => {
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);

  if (!product) return null;

  const handleGetQuote = () => {
    onClose();
    navigate('/contact', { state: { productOfInterest: product.name, sku: product.sku } });
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  const currentImage = product.images && product.images.length > 0 ? product.images[activeImage] : null;

  return ReactDOM.createPortal(
    <div className='fixed inset-0 z-9999 flex items-center justify-center p-4 animate-in fade-in duration-200'>
      <div className='absolute inset-0 bg-secondary/90 backdrop-blur-sm' onClick={onClose}></div>

      {/* ZOOM OVERLAY */}
      {isZoomed && currentImage && (
        <div className="fixed inset-0 z-10000 bg-black flex items-center justify-center animate-in zoom-in-95 duration-200" onClick={() => setIsZoomed(false)}>
          <button aria-label="Close zoom view" onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }} className='absolute top-6 right-6 z-20 p-3 bg-white/10 hover:bg-white text-white hover:text-black rounded-full transition-colors backdrop-blur-md'>
            <X className='w-8 h-8' />
          </button>
          <img
            src={optimizeImage(currentImage, 1600)}
            alt={product.name}
            className="max-w-full max-h-full object-contain p-4 md:p-10 select-none"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className='relative bg-primary w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl flex flex-col md:flex-row overflow-hidden animate-in zoom-in-95 duration-300'>
        <button aria-label="Close modal" onClick={onClose} className='absolute top-4 right-4 z-20 p-2 bg-gray-100 hover:bg-primary hover:text-white rounded-full transition-colors'>
          <X className='w-5 h-5' />
        </button>

        {/* IMAGE SECTION */}
        <div className='w-full md:w-1/2 bg-primary p-8 flex flex-col items-center justify-center select-none'>
          <div
            className={`relative aspect-square w-full max-w-sm mb-4 bg-white rounded-sm shadow-sm p-4 flex items-center justify-center group ${currentImage ? 'cursor-zoom-in' : ''}`}
            onClick={() => currentImage && setIsZoomed(true)}
          >
            {currentImage ? (
              <>
                <img
                  src={optimizeImage(currentImage, 800)}
                  srcSet={generateSrcSet(currentImage)}
                  sizes="(max-width: 768px) 100vw, 800px"
                  alt={product.name}
                  className='w-full h-full object-contain mix-blend-multiply'
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/5 rounded-sm">
                  <div className="bg-white/90 text-secondary px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest shadow-sm flex items-center gap-2">
                    <ZoomIn className="w-3 h-3" /> Zoom
                  </div>
                </div>
              </>
            ) : (
              <span className='text-gray-700 font-bold uppercase tracking-widest'>No Image</span>
            )}
          </div>
          {product.images && product.images.length > 1 && (
            <div className='flex gap-2 overflow-x-auto pt-2 pb-2 w-full justify-center custom-img-scrollbar'>
              {product.images.map((img, idx) => (
                <button
                  aria-label={`View image ${idx + 1}`}
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  className={`w-14 h-14 border-2 rounded-sm overflow-hidden shrink-0 transition-all ${activeImage === idx ? 'border-white' : 'border-primary hover:border-white opacity-70 hover:opacity-100'}`}
                >
                  <img src={optimizeImage(img, 100)} alt={`${product.name} thumbnail ${idx + 1}`} className='w-full h-full object-cover' />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* DETAILS SECTION */}
        <div className='w-full md:w-1/2 p-8 md:p-10 flex flex-col bg-white'>
          <div className='mb-auto'>
            <div className='flex flex-wrap items-center gap-2 mb-4'>
              <span className='px-3 py-1 bg-primary text-white text-[10px] font-bold uppercase tracking-widest rounded-sm'>
                {product.brand}
              </span>
              <span className='px-3 py-1 bg-secondary text-white text-[10px] font-bold uppercase tracking-widest rounded-sm'>
                SKU: {product.sku}
              </span>
            </div>
            <h2 className='font-heading text-3xl md:text-4xl text-secondary uppercase leading-none mb-4'>{product.name}</h2>

            <div className='mb-4 pb-4 border-b border-gray-400 space-y-2'>
              <div className='flex flex-wrap items-center text-xs font-bold text-gray-400 gap-1 uppercase tracking-wide'>
                <span className='text-primary'>Category:</span>
                {product.categoryPath.map((cat, i) => (
                  <span key={i} className='flex items-center text-gray-600'>
                    {i > 0 && <ChevronRight className='w-3 h-3 mx-1 text-gray-600' />}
                    {cat}
                  </span>
                ))}
              </div>

              {product.sizeDisplay && (
                <div className='flex items-center text-xs font-bold text-gray-400 gap-2 uppercase tracking-wide'>
                  <span className='text-primary'>Size:</span>
                  <span className='text-white font-bold bg-primary px-2 py-0.5 rounded-sm'>{product.sizeDisplay}</span>
                </div>
              )}
              {product.colorDisplay && (
                <div className='flex items-center text-xs font-bold text-gray-400 gap-2 capitalize tracking-wide'>
                  <span className='text-primary'>Color:</span>
                  <span className='text-white font-bold bg-primary px-2 py-0.5 rounded-sm'>{product.colorDisplay}</span>
                </div>
              )}
              {product.ignitionDisplay && (
                <div className='flex items-center text-xs font-bold text-gray-400 gap-2 capitalize tracking-wide'>
                  <span className='text-primary'>Ignition:</span>
                  <span className='text-white font-bold bg-primary px-2 py-0.5 rounded-sm'>{product.ignitionDisplay}</span>
                </div>
              )}
              {product.powerVoltageDisplay && (
                <div className='flex items-start text-xs font-bold text-gray-400 gap-2 uppercase tracking-wide'>
                  <span className='text-primary flex items-center gap-1 mt-0.5 shrink-0'>
                    <Zap className="w-3 h-3" /> Power:
                  </span>
                  <span className='text-white font-bold bg-primary px-2 py-0.5 rounded-sm leading-relaxed'>
                    {product.powerVoltageDisplay}
                  </span>
                </div>
              )}
            </div>

            <div className='product-description max-h-52 overflow-y-auto overflow-x-auto custom-scrollbar pr-2' dangerouslySetInnerHTML={{ __html: product.description || "No description available." }} />
          </div>
          <div className='mt-6 pt-0'>
            <button onClick={handleGetQuote} className='w-full py-4 bg-primary text-white rounded-xl uppercase font-bold tracking-widest text-sm hover:bg-secondary transition-colors shadow-lg flex items-center justify-center gap-2'>
              Get a Quote <ArrowRight className='w-4 h-4' />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// --- FILTER COMPONENT ---
const CategoryFilter = ({ processedData, selectedCategoryPath, onSelectCategory }) => {
  if (selectedCategoryPath.length === 0) {
    return (
      <div className="space-y-1">
        {MAIN_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => onSelectCategory([cat])}
            className="w-full text-left px-3 py-2 text-sm font-bold text-gray-600 hover:text-white hover:bg-primary rounded-sm transition-all flex justify-between items-center group"
          >
            {cat}
            <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-white" />
          </button>
        ))}
      </div>
    );
  }

  const currentLevelSubs = useMemo(() => {
    const subs = new Set();
    processedData.products.forEach(p => {
      const matchesPath = selectedCategoryPath.every((part, i) => p.categoryPath[i] === part);
      if (matchesPath && p.categoryPath.length > selectedCategoryPath.length) {
        subs.add(p.categoryPath[selectedCategoryPath.length]);
      }
    });
    return Array.from(subs).sort();
  }, [processedData, selectedCategoryPath]);

  return (
    <div className="space-y-4 animate-in slide-in-from-left-2 duration-300">
      <div className="flex flex-col gap-2 mb-4 pb-4 border-b border-gray-200/50">
        <button
          onClick={() => onSelectCategory([])}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-primary transition-colors self-start"
        >
          <Home className="w-3 h-3" /> All Categories
        </button>

        <div className="flex flex-wrap items-center gap-1 text-xs font-bold uppercase tracking-wide text-secondary">
          {selectedCategoryPath.map((part, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
              <button
                onClick={() => onSelectCategory(selectedCategoryPath.slice(0, index + 1))}
                className={`${index === selectedCategoryPath.length - 1 ? 'text-primary underline decoration-primary/30 underline-offset-4' : 'hover:text-primary'} transition-colors`}
              >
                {part}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        {currentLevelSubs.length > 0 ? (
          <>
            <div className="text-[10px] font-black text-gray-400 mb-2 uppercase tracking-[0.2em] px-3">Subcategories</div>
            {currentLevelSubs.map(sub => (
              <button
                key={sub}
                onClick={() => onSelectCategory([...selectedCategoryPath, sub])}
                className="w-full text-left px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 border-l-2 border-transparent hover:border-primary transition-all flex items-center gap-2"
              >
                {sub}
              </button>
            ))}
          </>
        ) : (
          <div className="text-sm text-gray-400 italic px-3 py-2 bg-gray-50 rounded-sm text-center">No further subcategories</div>
        )}
      </div>
    </div>
  );
};

// --- REUSABLE CHECKBOX LIST COMPONENT ---
const FilterList = ({ title, items, selectedItems, onItemToggle, emptyMessage = "No options available" }) => (
  <div className="mb-6 border-b border-gray-100 pb-8 last:border-0 last:pb-0">
    <h2 className="font-heading text-lg uppercase text-secondary font-bold mb-4">{title}</h2>
    <div className="space-y-2 custom-scrollbar max-h-60 overflow-y-auto pr-2">
      {items.length > 0 ? items.map((item) => (
        <label key={item.value} className="flex items-center gap-3 cursor-pointer group select-none py-1 hover:bg-gray-50 rounded-sm px-1 -mx-1 transition-colors">
          <div className={`w-4 h-4 border rounded-xs flex items-center justify-center transition-all duration-200 ${selectedItems.includes(item.value) ? 'bg-primary border-primary' : 'border-gray-300 group-hover:border-primary'}`}>
            {selectedItems.includes(item.value) && <Check className='w-3 h-3 text-white' strokeWidth={4} />}
          </div>
          <input type="checkbox" className="hidden" checked={selectedItems.includes(item.value)} onChange={() => onItemToggle(item.value)} />
          <span className={`text-sm font-sans transition-colors flex-1 ${selectedItems.includes(item.value) ? 'text-secondary font-bold' : 'text-gray-600 group-hover:text-primary'}`}>
            {item.label}
          </span>
          <span className="text-[10px] font-bold text-gray-700 bg-gray-200 px-1.5 py-0.5 rounded-full">{item.count}</span>
        </label>
      )) : (
        <div className="text-sm text-gray-400 italic">{emptyMessage}</div>
      )}
    </div>
  </div>
);


// --- MAIN PAGE ---
// Define constant outside component
const ITEMS_PER_PAGE = 24;

const ProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const { products: fetchedProducts, loading: dataLoading } = useProductsData();

  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(() => {
    const p = searchParams.get('page');
    const parsed = parseInt(p, 10);
    return (parsed && !isNaN(parsed) && parsed > 0) ? parsed : 1;
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const mainGridRef = useRef(null);

  // -- Initialize State from URL Params --
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || "");
  const [selectedCategoryPath, setSelectedCategoryPath] = useState(() => searchParams.get('category') ? searchParams.get('category').split(',') : []);
  const [selectedBrands, setSelectedBrands] = useState(() => searchParams.get('brand') ? searchParams.get('brand').split(',') : []);
  const [selectedSizes, setSelectedSizes] = useState(() => searchParams.get('size') ? searchParams.get('size').split(',') : []);
  const [selectedColors, setSelectedColors] = useState(() => searchParams.get('color') ? searchParams.get('color').split(',') : []);
  const [selectedIgnitions, setSelectedIgnitions] = useState(() => searchParams.get('ignition') ? searchParams.get('ignition').split(',') : []);

  const loadMoreRef = useRef(null);

  // --- SYNC FETCHED DATA ---
  useEffect(() => {
    if (!dataLoading && fetchedProducts.length > 0) {
      setRawData(fetchedProducts);
      setLoading(false);
    }
  }, [dataLoading, fetchedProducts]);

  // --- PROCESS DATA ---
  const processedData = useMemo(() => {
    // 1. First Pass: Collect all unique brands
    const uniqueBrands = new Set();
    rawData.forEach(p => {
      if (p.company) uniqueBrands.add(p.company.trim().toLowerCase());
    });

    // 2. Second Pass: Process products
    const cleanProducts = rawData.map(p => {
      const { main, subs } = parseCategoryPath(p.filters || "", uniqueBrands);

      // Extract Arrays using Helper
      const sizeArr = normalizeAttribute(p.sizes || p.size);
      const colorArr = normalizeAttribute(p.color || p.colors);
      const ignitionArr = normalizeAttribute(p.ignition || p.ignitions);

      // Process Power/Voltage
      const pvDisplay = formatPowerVoltage(p["power voltage"] || p.power_voltage);

      return {
        ...p,
        id: p._id?.$oid || p._id || Math.random(),
        brand: p.company ? p.company.trim() : 'Other',
        sizeDisplay: sizeArr.join(', '),
        colorDisplay: colorArr.join(', '),
        ignitionDisplay: ignitionArr.join(', '),
        powerVoltageDisplay: pvDisplay,
        sizes: sizeArr,
        colors: colorArr,
        ignitions: ignitionArr,
        mainCategory: main,
        subCategories: subs,
        categoryPath: [main, ...subs],
      };
    });

    return { products: cleanProducts };
  }, [rawData]);

  // --- HANDLE BUTTON CLICK FROM HOME ---
  useEffect(() => {
    if (!loading && location.state) {
      const state = location.state;

      if (state.categoryPath) {
        const pathExists = processedData.products.some(p =>
          state.categoryPath.every((part, i) => p.categoryPath[i] === part)
        );

        if (pathExists) {
          setSelectedCategoryPath(state.categoryPath);
          setSearchQuery("");
        } else {
          const searchTerm = state.categoryPath[state.categoryPath.length - 1];
          setSearchQuery(searchTerm);
          setSelectedCategoryPath([]);
        }
      }

      else if (state.category) {
        setSelectedCategoryPath([state.category]);
        setSearchQuery("");
      }

      if (state.brand) {
        setSelectedBrands([state.brand]);
      }

      window.history.replaceState({}, document.title);
    }
  }, [location, loading, processedData]);

  // --- FILTERING ---
  const filteredProducts = useMemo(() => {
    let result = processedData.products;

    if (selectedCategoryPath.length > 0) {
      result = result.filter(p => selectedCategoryPath.every((part, i) => p.categoryPath[i] === part));
    }

    if (selectedBrands.length > 0) {
      result = result.filter(p => selectedBrands.includes(p.brand));
    }

    if (selectedSizes.length > 0) {
      result = result.filter(p => p.sizes && p.sizes.some(s => selectedSizes.includes(s)));
    }

    if (selectedColors.length > 0) {
      result = result.filter(p => p.colors && p.colors.some(c => selectedColors.includes(c)));
    }

    if (selectedIgnitions.length > 0) {
      result = result.filter(p => p.ignitions && p.ignitions.some(i => selectedIgnitions.includes(i)));
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.filters.toLowerCase().includes(q));
    }

    return result;
  }, [processedData, selectedCategoryPath, selectedBrands, selectedSizes, selectedColors, selectedIgnitions, searchQuery]);

  // --- SYNC URL TO STATE (Back Button Support) ---
  useEffect(() => {
    const p = searchParams.get('page');
    const parsed = parseInt(p, 10);
    const newPage = (parsed && !isNaN(parsed) && parsed > 0) ? parsed : 1;
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  }, [searchParams]);

  // --- SYNC STATE TO URL ---
  useEffect(() => {
    // Prevent syncing if we are currently processing a navigation state update to avoid overwriting
    if (location.state) return;

    const params = {};
    if (currentPage > 1) params.page = currentPage;
    if (searchQuery) params.q = searchQuery;
    if (selectedCategoryPath.length > 0) params.category = selectedCategoryPath.join(',');
    if (selectedBrands.length > 0) params.brand = selectedBrands.join(',');
    if (selectedSizes.length > 0) params.size = selectedSizes.join(',');
    if (selectedColors.length > 0) params.color = selectedColors.join(',');
    if (selectedIgnitions.length > 0) params.ignition = selectedIgnitions.join(',');

    setSearchParams(params, { replace: true });
  }, [currentPage, searchQuery, selectedCategoryPath, selectedBrands, selectedSizes, selectedColors, selectedIgnitions, setSearchParams, location.state]);

  // --- AGGREGATION HELPER ---
  const getAvailableOptions = (attributeKey, currentSelection) => {
    const counts = {};
    const categoryFiltered = processedData.products.filter(p => {
      if (selectedCategoryPath.length > 0) {
        return selectedCategoryPath.every((part, i) => p.categoryPath[i] === part);
      }
      return true;
    });

    categoryFiltered.forEach(p => {
      const values = attributeKey === 'brand' ? [p.brand] : (p[attributeKey] || []);
      values.forEach(val => {
        if (val) counts[val] = (counts[val] || 0) + 1;
      });
    });

    return Object.entries(counts)
      .map(([k, v]) => ({ value: k, label: k, count: v }))
      .sort((a, b) => {
        // Sort: Selected first, then count desc, then alpha
        const isASelected = currentSelection.includes(a.value);
        const isBSelected = currentSelection.includes(b.value);
        if (isASelected && !isBSelected) return -1;
        if (!isASelected && isBSelected) return 1;

        if (attributeKey === 'sizes') {
          const numA = parseFloat(a.value);
          const numB = parseFloat(b.value);
          if (!isNaN(numA) && !isNaN(numB) && numA !== numB) return numA - numB;
        }

        if (a.count !== b.count) return b.count - a.count;
        return a.value.localeCompare(b.value, undefined, { numeric: true });
      });
  };

  const availableBrands = useMemo(() => getAvailableOptions('brand', selectedBrands), [processedData, selectedCategoryPath, selectedBrands]);
  const availableSizes = useMemo(() => getAvailableOptions('sizes', selectedSizes), [processedData, selectedCategoryPath, selectedSizes]);
  const availableColors = useMemo(() => getAvailableOptions('colors', selectedColors), [processedData, selectedCategoryPath, selectedColors]);
  const availableIgnitions = useMemo(() => getAvailableOptions('ignitions', selectedIgnitions), [processedData, selectedCategoryPath, selectedIgnitions]);

  // -- RESET PAGE ON FILTER CHANGE --
  // We use a "hash" of the filters to ensure we ONLY reset the page when filters ACTUALLY content-change.
  // This prevents resets on mount, back-navigation, or reference updates that don't change values.
  const filterHash = useMemo(() => {
    return [
      selectedCategoryPath.join(','),
      selectedBrands.sort().join(','),
      selectedSizes.sort().join(','),
      selectedColors.sort().join(','),
      selectedIgnitions.sort().join(','),
      searchQuery
    ].join('|');
  }, [selectedCategoryPath, selectedBrands, selectedSizes, selectedColors, selectedIgnitions, searchQuery]);

  const prevFilterHash = useRef(filterHash);

  useEffect(() => {
    if (filterHash !== prevFilterHash.current) {
      setCurrentPage(1);
      prevFilterHash.current = filterHash;
    }
  }, [filterHash]);

  // Scroll to top of grid when page changes
  useEffect(() => {
    if (mainGridRef.current) {
      mainGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);



  // -- Toggles --
  const toggleSelection = (item, currentList, setList) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const clearAllFilters = () => {
    setSelectedCategoryPath([]);
    setSelectedBrands([]);
    setSelectedSizes([]);
    setSelectedColors([]);
    setSelectedIgnitions([]);
    setSearchQuery("");
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center pt-[72px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );

  const activeCategory = selectedCategoryPath.length > 0 ? selectedCategoryPath[selectedCategoryPath.length - 1] : '';
  const activeBrand = selectedBrands.length === 1 ? selectedBrands[0] : '';

  let dynamicTitle = activeCategory ? `${activeCategory} Collection` : "Premium Fireplace Collection";
  if (activeBrand) dynamicTitle = `${activeBrand} ${dynamicTitle}`;
  if (searchQuery) dynamicTitle = `Search: ${searchQuery} - ${dynamicTitle}`;
  if (currentPage > 1) dynamicTitle += ` | Page ${currentPage}`;

  let dynamicDescription = `Browse our selection of ${activeBrand || 'premium'} ${activeCategory?.toLowerCase() || 'fireplaces, grills, and outdoor living features'}.`;
  if (currentPage > 1) dynamicDescription += ` Page ${currentPage} of our catalog.`;
  else dynamicDescription += ` Filter by fuel type, brand, and size. Serving Langley & Greater Vancouver.`;

  return (
    <div className='min-h-screen pt-[72px] bg-white animate-fade-in'>
      <SEO
        title={dynamicTitle}
        description={dynamicDescription}
        canonical={currentPage > 1 || activeBrand || activeCategory || searchQuery ? undefined : `${SITE_URL}/products`}
        keywords={`fireplaces, ${selectedCategoryPath.join(', ')}, ${activeBrand}, premium grills`}
      />
      <Schema
        type="BreadcrumbList"
        data={{
          items: [
            { name: 'Home', path: '/' },
            { name: 'Products', path: '/products' },
            ...(selectedCategoryPath.map((cat, i) => ({
              name: cat,
              path: `/products?category=${selectedCategoryPath.slice(0, i + 1).join(',')}`
            })))
          ]
        }}
      />
      <Schema
        type="CollectionPage"
        data={{
          name: dynamicTitle,
          description: dynamicDescription,
          url: `${SITE_URL}${location.pathname}${location.search}`
        }}
      />
      {filteredProducts.length > 0 && (
        <Schema
          type="ItemList"
          data={{
            items: filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(p => ({
              url: `${SITE_URL}/products/${slugify(p.name)}`
            }))
          }}
        />
      )}
      <section className='relative w-full h-[40vh] min-h-[300px] flex items-center justify-center bg-secondary overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src={optimizeImage(`${import.meta.env.BASE_URL}images/headers/products.avif`, 1200)}
            srcSet={generateSrcSet(`${import.meta.env.BASE_URL}images/headers/products.avif`)}
            sizes="100vw"
            alt="Browse premium gas fireplaces, electric fireplaces, and outdoor living products"
            width={1200}
            height={800}
            fetchPriority="high"
            className='w-full h-full object-cover opacity-30'
          />
          <div className='absolute inset-0 bg-linear-to-t from-secondary via-secondary/70 to-transparent'></div>
        </div>

        <div className='relative z-10 text-center px-6 max-w-4xl mx-auto'>
          <div className='flex items-center justify-center flex-wrap gap-2 text-xs uppercase tracking-widest text-white/70 mb-4 font-bold'>
            <Link to="/" className='hover:text-primary transition-colors'>Home</Link>
            <ChevronRight className='w-3 h-3 text-primary' />
            <button
              onClick={() => setSelectedCategoryPath([])}
              className={`hover:text-primary transition-colors ${selectedCategoryPath.length === 0 ? 'text-white' : ''}`}
            >
              Products
            </button>
            {selectedCategoryPath.map((part, i) => (
              <React.Fragment key={i}>
                <ChevronRight className='w-3 h-3 text-primary' />
                <button
                  onClick={() => setSelectedCategoryPath(selectedCategoryPath.slice(0, i + 1))}
                  className='text-white hover:text-primary transition-colors border-b border-transparent hover:border-primary'
                >
                  {part}
                </button>
              </React.Fragment>
            ))}
          </div>

          <h1 className='font-heading text-4xl md:text-6xl text-white uppercase mb-4 drop-shadow-lg'>
            {selectedCategoryPath.length > 0 ? (
              <span className='animate-in fade-in slide-in-from-bottom duration-500'>
                {selectedCategoryPath[selectedCategoryPath.length - 1]}
              </span>
            ) : (
              <span>Premium <span className='text-primary'>Selection</span></span>
            )}
          </h1>
        </div>
      </section>

      {/* MOBILE FILTERS TOGGLE */}
      <div className='lg:hidden border-b border-gray-100 bg-white sticky top-[72px] z-30 shadow-xs'>
        <div className='p-4 space-y-4'>
          <div className="relative group w-full">
            <input
              type="text" placeholder="Search Brand/Product Name..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-sm py-3 pl-10 pr-4 text-sm focus:outline-hidden focus:border-primary transition-colors focus:ring-1 focus:ring-primary/20 shadow-sm"
            />
            <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400 group-focus-within:text-primary transition-colors" />
          </div>

          <div className='flex justify-between items-center'>
            <button onClick={() => setIsMobileFilterOpen(true)} className='flex items-center gap-2 uppercase font-bold text-xs tracking-widest text-secondary border border-gray-300 px-4 py-3 rounded-sm'>
              <SlidersHorizontal className='w-4 h-4' /> Filters
            </button>
            <span className='text-xs text-gray-500 font-bold tracking-wide'>{filteredProducts.length} Products</span>
          </div>
        </div>
      </div>

      <div className='max-w-[1600px] mx-auto flex flex-col lg:flex-row min-h-screen relative'>
        {/* DESKTOP SIDEBAR */}
        <aside className='hidden lg:block w-72 shrink-0 py-12 pr-12 pl-6 border-r border-gray-100 sticky top-[72px] h-[calc(100vh-72px)] overflow-y-auto custom-scrollbar'>
          <div className='flex items-center justify-between mb-8'>
            <div className='flex items-center gap-2 text-primary font-bold uppercase tracking-widest text-xs'>
              <SlidersHorizontal className='w-4 h-4' /> Filters
            </div>
            <button onClick={clearAllFilters} className='text-[10px] uppercase font-bold text-gray-600 hover:text-primary tracking-widest transition-colors'>
              Clear All
            </button>
          </div>

          <div className="relative mb-8 group">
            <input
              type="text" placeholder="Enter Brand/Product Name..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-sm py-3 pl-10 pr-4 text-sm focus:outline-hidden focus:border-primary transition-colors focus:ring-1 focus:ring-primary/20 shadow-sm"
            />
            <Search className="absolute left-3 top-3.5 w-4 h-4 text-gray-400 group-focus-within:text-primary transition-colors" />
          </div>

          <div className="border-b border-gray-100 pb-8 mb-6">
            <h2 className="font-heading text-lg uppercase text-secondary font-bold mb-4">Categories</h2>
            <CategoryFilter
              processedData={processedData}
              selectedCategoryPath={selectedCategoryPath}
              onSelectCategory={setSelectedCategoryPath}
            />
          </div>

          <FilterList
            title="Brands"
            items={availableBrands}
            selectedItems={selectedBrands}
            onItemToggle={(val) => toggleSelection(val, selectedBrands, setSelectedBrands)}
          />

          <FilterList
            title="Size"
            items={availableSizes}
            selectedItems={selectedSizes}
            onItemToggle={(val) => toggleSelection(val, selectedSizes, setSelectedSizes)}
            emptyMessage="No sizes available"
          />

          <FilterList
            title="Color"
            items={availableColors}
            selectedItems={selectedColors}
            onItemToggle={(val) => toggleSelection(val, selectedColors, setSelectedColors)}
            emptyMessage="No colors available"
          />

          <FilterList
            title="Ignition"
            items={availableIgnitions}
            selectedItems={selectedIgnitions}
            onItemToggle={(val) => toggleSelection(val, selectedIgnitions, setSelectedIgnitions)}
            emptyMessage="No ignition types available"
          />
        </aside>

        {/* MAIN GRID */}
        <div className='flex-1 p-6 md:p-12 bg-white'>
          <h2 className='sr-only'>Product Results</h2>
          <div className='hidden lg:flex justify-between items-center mb-10 pb-4 border-b border-gray-100'>
            <span className='text-sm text-gray-500 font-sans font-medium'>
              Showing <strong>{Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length)}</strong> of <strong>{filteredProducts.length}</strong> Products
            </span>
          </div>

          {filteredProducts.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-32 text-center'>
              <Search className="w-16 h-16 text-gray-200 mb-6" />
              <h2 className="font-heading text-2xl text-secondary uppercase mb-2">No Matches Found</h2>
              <p className="text-gray-500 max-w-md mb-8">Try adjusting your filters or search query.</p>
              <button onClick={clearAllFilters} className='px-8 py-3 bg-primary text-white uppercase font-bold tracking-widest rounded-sm hover:bg-secondary transition-colors'>
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
              <div ref={mainGridRef} className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 scroll-mt-[100px]'>
                {filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((product) => (
                  <Link key={product.id} to={`/products/${slugify(product.name)}`} className='group flex flex-col bg-white border border-gray-300 hover:border-primary hover:shadow-xl transition-all duration-300 rounded-sm overflow-hidden animate-fade-in'>
                    <div className='relative aspect-4/3 bg-gray-100 overflow-hidden'>
                      {product.images && product.images.length > 0 ? (
                        <img
                          src={optimizeImage(product.images[0], 400)}
                          srcSet={generateSrcSet(product.images[0])}
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
                          alt={product.name}
                          width={400}
                          height={300}
                          className='w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105'
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-100"><span className="text-xs uppercase font-bold tracking-widest">No Image</span></div>
                      )}
                      <div className='absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300'></div>
                      <div className='absolute bottom-0 left-0 w-full translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out'>
                        <button
                          className='w-full py-3 bg-primary text-white text-xs uppercase font-bold tracking-widest hover:bg-secondary transition-colors'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedProduct(product);
                          }}
                        >
                          Quick View
                        </button>
                      </div>
                    </div>
                    <div className='flex flex-col flex-1 p-5'>
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className='text-[10px] text-gray-500 uppercase tracking-widest font-bold group-hover:text-primary transition-colors mt-0.5'>{product.brand}</div>

                        <div className="flex flex-col items-end gap-1">
                          {product.sizeDisplay && <div className='text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm font-bold text-right'>{product.sizeDisplay}</div>}
                          {product.colorDisplay && <div className='text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm font-bold text-right'>{product.colorDisplay}</div>}
                          {product.powerVoltageDisplay && (
                            <div className='text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-sm font-bold text-right flex items-center gap-1'>
                              <Zap className="w-3 h-3 text-primary" /> {product.powerVoltageDisplay}
                            </div>
                          )}
                        </div>
                      </div>
                      <h3 className='font-heading text-lg text-secondary leading-tight mb-3 line-clamp-2 group-hover:text-primary transition-colors'>{product.name}</h3>
                      <div className='mt-auto pt-4 border-t border-gray-100 flex items-center justify-between'>
                        <span className='text-[10px] font-bold uppercase tracking-widest text-primary'>View Details</span>
                        <ArrowRight className='w-4 h-4 text-primary transition-transform group-hover:translate-x-1' />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* PAGINATION CONTROLS */}
              {filteredProducts.length > ITEMS_PER_PAGE && (
                <div className="mt-12 flex justify-center items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Previous Page"
                    className="p-2 border border-gray-300 rounded-sm hover:border-primary hover:text-primary disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:text-gray-400 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <div className="flex gap-1 items-center">
                    {(() => {
                      const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
                      let pages = [];
                      if (totalPages <= 7) {
                        pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                      } else {
                        if (currentPage <= 4) pages = [1, 2, 3, 4, 5, '...', totalPages];
                        else if (currentPage >= totalPages - 3) pages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                        else pages = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
                      }

                      return pages.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => typeof p === 'number' && setCurrentPage(p)}
                          disabled={typeof p !== 'number'}
                          className={`w-10 h-10 flex items-center justify-center text-sm font-bold rounded-sm transition-colors ${p === currentPage
                            ? 'bg-primary text-white'
                            : typeof p === 'number'
                              ? 'hover:bg-gray-100 text-gray-600'
                              : 'text-gray-400 cursor-default'
                            }`}
                        >
                          {p}
                        </button>
                      ));
                    })()}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredProducts.length / ITEMS_PER_PAGE), p + 1))}
                    disabled={currentPage === Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)}
                    aria-label="Next Page"
                    className="p-2 border border-gray-300 rounded-sm hover:border-primary hover:text-primary disabled:opacity-30 disabled:hover:border-gray-300 disabled:hover:text-gray-400 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedProduct && <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />}

      {isMobileFilterOpen && ReactDOM.createPortal(
        <div className='fixed inset-0 z-9999 flex flex-col animate-in slide-in-from-bottom duration-300'>
          <div className="relative z-50">
            <Header />
          </div>

          <div className='flex-1 overflow-y-auto bg-white pt-[72px] md:pt-20'>
            <div className='px-6'>
              <div className='flex items-center justify-between py-3 border-b border-gray-300 bg-white shrink-0 safe-top'>
                <div className='flex items-center gap-3'>
                  <button aria-label="Close filters" onClick={() => setIsMobileFilterOpen(false)} className='p-2 bg-gray-200 text-primary rounded-full hover:bg-primary hover:text-white transition-colors'>
                    <X className='w-4 h-4' />
                  </button>
                  <span className='font-heading font-bold text-xl uppercase text-primary'>Filters</span>
                </div>
                <button onClick={clearAllFilters} className='text-xs font-bold uppercase tracking-widest text-primary border-b border-primary pb-0.5'>Clear All</button>
              </div>

              <div className="pt-2 mb-4">
                <h2 className="font-heading text-lg uppercase text-secondary font-bold mb-4">Categories</h2>
                <CategoryFilter processedData={processedData} selectedCategoryPath={selectedCategoryPath} onSelectCategory={setSelectedCategoryPath} />
              </div>

              <FilterList
                title="Brands"
                items={availableBrands}
                selectedItems={selectedBrands}
                onItemToggle={(val) => toggleSelection(val, selectedBrands, setSelectedBrands)}
              />

              <FilterList
                title="Size"
                items={availableSizes}
                selectedItems={selectedSizes}
                onItemToggle={(val) => toggleSelection(val, selectedSizes, setSelectedSizes)}
                emptyMessage="No sizes available"
              />

              <FilterList
                title="Color"
                items={availableColors}
                selectedItems={selectedColors}
                onItemToggle={(val) => toggleSelection(val, selectedColors, setSelectedColors)}
                emptyMessage="No colors available"
              />

              <FilterList
                title="Ignition"
                items={availableIgnitions}
                selectedItems={selectedIgnitions}
                onItemToggle={(val) => toggleSelection(val, selectedIgnitions, setSelectedIgnitions)}
                emptyMessage="No ignition types available"
              />

              <div className='p-4 border-t border-gray-300 bg-white shrink-0 safe-bottom'>
                <button onClick={() => setIsMobileFilterOpen(false)} className='w-full py-4 bg-primary text-white uppercase font-bold tracking-widest rounded-sm shadow-lg hover:bg-secondary transition-colors'>
                  View {filteredProducts.length} Products
                </button>
              </div>
            </div>

            <Footer />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductPage;